import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, CheckCircle2, Circle, ChevronDown, ChevronUp,
  Store, ShieldCheck, CreditCard, Package, Globe,
  ArrowRight, Loader2, AlertCircle, PartyPopper, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Progress is stored in localStorage
const PROGRESS_KEY = 'gizi_seller_onboarding_progress';

const DEFAULT_STEPS = [
  {
    id: 1,
    icon: Store,
    title: 'Buat Akun MPStore',
    description: 'Pastikan Anda sudah memiliki akun MPStore yang aktif.',
    instructions: [
      'Kunjungi website MPStore atau download aplikasi MPStore di Play Store / App Store.',
      'Daftarkan diri Anda dengan mengisi data pribadi dan data usaha Anda.',
      'Verifikasi email dan nomor telepon yang Anda daftarkan.',
      'Setelah berhasil, Anda akan mendapatkan akun MPStore.',
    ],
    image_url: null,
  },
  {
    id: 2,
    icon: ShieldCheck,
    title: 'Verifikasi MPStore',
    description: 'Lakukan proses verifikasi identitas dan data usaha Anda di MPStore.',
    instructions: [
      'Login ke dashboard MPStore Anda.',
      'Masuk ke menu "Verifikasi" atau "Pengaturan Akun".',
      'Upload dokumen yang diperlukan (KTP, NPWP, dll).',
      'Tunggu proses verifikasi selesai (biasanya 1-3 hari kerja).',
    ],
    image_url: null,
  },
  {
    id: 3,
    icon: CreditCard,
    title: 'Aktifkan Kasir MPStore',
    description: 'Siapkan dan aktifkan fitur Kasir untuk mengelola penjualan Anda.',
    instructions: [
      'Buka dashboard MPStore → menu "Kasir".',
      'Aktifkan fitur Kasir MPStore.',
      'Konfigurasikan metode pembayaran yang ingin diterima.',
      'Lakukan tes transaksi untuk memastikan semuanya berjalan lancar.',
    ],
    image_url: null,
  },
  {
    id: 4,
    icon: Package,
    title: 'Upload Product',
    description: 'Upload produk-produk yang ingin Anda jualkan di marketplace.',
    instructions: [
      'Masuk ke menu "Produk" di dashboard MPStore.',
      'Klik "Tambah Produk" dan isi detail produk (nama, harga, stok, deskripsi).',
      'Upload foto produk berkualitas tinggi (rekomendasi: minimal 3 foto per produk).',
      'Atur kategori produk dengan tepat agar mudah ditemukan pembeli.',
      'Simpan dan pastikan produk berhasil ditampilkan.',
    ],
    image_url: null,
  },
  {
    id: 5,
    icon: Globe,
    title: 'Aktivasi Miniweb & Online-kan Produk',
    description: 'Aktifkan Miniweb MPStore dan online-kan produk kasir ke miniweb Anda.',
    instructions: [
      'Buka menu "Miniweb" di dashboard MPStore.',
      'Aktifkan fitur Miniweb untuk toko Anda.',
      'Pilih produk dari kasir yang ingin ditampilkan di miniweb.',
      'Klik "Online-kan Produk" untuk setiap produk yang ingin dijual online.',
      'Pastikan semua produk sudah tampil dengan benar di miniweb Anda.',
    ],
    image_url: null,
  },
];

const StepCard = ({ step, index, isCompleted, isExpanded, onToggleComplete, onToggleExpand }) => {
  const StepIcon = step.icon || Store;
  const cardRef = useRef(null);

  return (
    <div
      ref={cardRef}
      className={`
        relative group
        transition-all duration-300 ease-out
      `}
      data-testid={`onboarding-step-${step.id}`}
    >
      {/* Connector line */}
      {index < 4 && (
        <div className={`
          absolute left-7 md:left-8 top-20 w-0.5 h-[calc(100%-40px)]
          transition-colors duration-500
          ${isCompleted ? 'bg-emerald-400' : 'bg-gray-200'}
        `} />
      )}

      {/* Step card */}
      <div className={`
        relative bg-white rounded-2xl border overflow-hidden
        transition-all duration-300
        ${isCompleted
          ? 'border-emerald-200 shadow-sm shadow-emerald-100'
          : 'border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200'
        }
      `}>
        {/* Header (always visible) */}
        <div
          className="flex items-center gap-4 p-5 md:p-6 cursor-pointer select-none"
          onClick={onToggleExpand}
        >
          {/* Step number / check */}
          <button
            className={`
              relative flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center
              transition-all duration-300 active:scale-90
              ${isCompleted
                ? 'bg-emerald-500 text-white shadow-md shadow-emerald-200'
                : 'bg-gray-50 text-gray-400 border border-gray-200 hover:border-[#0052CC] hover:text-[#0052CC]'
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              onToggleComplete();
            }}
            aria-label={isCompleted ? 'Tandai belum selesai' : 'Tandai selesai'}
          >
            {isCompleted ? (
              <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7" />
            ) : (
              <span className="font-heading font-bold text-lg md:text-xl">{index + 1}</span>
            )}
          </button>

          {/* Title + description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <StepIcon className={`w-4 h-4 flex-shrink-0 ${isCompleted ? 'text-emerald-500' : 'text-[#0052CC]'}`} />
              <h3 className={`
                font-heading font-bold text-base md:text-lg leading-tight
                ${isCompleted ? 'text-emerald-700' : 'text-gray-900'}
              `}>
                {step.title}
              </h3>
            </div>
            <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{step.description}</p>
          </div>

          {/* Expand / collapse chevron */}
          <div className={`
            flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
            transition-colors
            ${isExpanded ? 'bg-gray-100' : 'bg-transparent group-hover:bg-gray-50'}
          `}>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expandable content */}
        <div className={`
          overflow-hidden transition-all duration-400 ease-out
          ${isExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-5 md:px-6 pb-6 pt-0">
            <div className="border-t border-gray-100 pt-5">
              {/* Instructions grid */}
              <div className={`
                ${step.image_url ? 'grid md:grid-cols-2 gap-6' : ''}
              `}>
                {/* Instructions list */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">Langkah-langkah:</p>
                  <ol className="space-y-3">
                    {step.instructions.map((instruction, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center text-xs font-bold mt-0.5">
                          {i + 1}
                        </span>
                        <span className="text-sm text-gray-600 leading-relaxed">{instruction}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Tutorial image (mockup) */}
                {step.image_url && (
                  <div className="flex items-center justify-center">
                    <div className="relative max-w-[280px] w-full">
                      <div className="rounded-[2rem] overflow-hidden border-4 border-gray-800 bg-gray-900 shadow-2xl">
                        <div className="w-full h-6 bg-gray-800 flex items-center justify-center">
                          <div className="w-20 h-3 bg-gray-700 rounded-full" />
                        </div>
                        <img
                          src={step.image_url}
                          alt={`Tutorial ${step.title}`}
                          className="w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Complete button */}
              {!isCompleted && (
                <button
                  onClick={onToggleComplete}
                  className="mt-5 flex items-center gap-2 text-sm font-semibold text-[#0052CC] hover:text-[#0043A8] transition-colors"
                >
                  <Circle className="w-4 h-4" />
                  Tandai sudah selesai
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SellerOnboardingPage() {
  const { t } = useLanguage();
  const [steps, setSteps] = useState(DEFAULT_STEPS);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [expandedStep, setExpandedStep] = useState(1);
  const [mpstoreId, setMpstoreId] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const verifyRef = useRef(null);

  // Load progress from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PROGRESS_KEY);
      if (saved) {
        setCompletedSteps(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading progress:', e);
    }
  }, []);

  // Fetch tutorial steps from backend (if available)
  useEffect(() => {
    const fetchSteps = async () => {
      try {
        const response = await axios.get(`${API}/seller/tutorial-steps`);
        if (response.data.steps && response.data.steps.length > 0) {
          // Merge backend data with default icons
          const iconMap = [Store, ShieldCheck, CreditCard, Package, Globe];
          const merged = response.data.steps.map((step, i) => ({
            ...DEFAULT_STEPS[i],
            ...step,
            icon: iconMap[i] || Store,
            instructions: step.detail_instructions
              ? step.detail_instructions.split('\n').filter(Boolean)
              : DEFAULT_STEPS[i]?.instructions || [],
          }));
          setSteps(merged);
        }
      } catch (error) {
        // Use defaults if API not available
        console.log('Using default tutorial steps');
      }
    };
    fetchSteps();
  }, []);

  // Save progress
  const saveProgress = (newCompleted) => {
    setCompletedSteps(newCompleted);
    localStorage.setItem(PROGRESS_KEY, JSON.stringify(newCompleted));
  };

  const toggleStepComplete = (stepId) => {
    const newCompleted = completedSteps.includes(stepId)
      ? completedSteps.filter(id => id !== stepId)
      : [...completedSteps, stepId];
    saveProgress(newCompleted);

    // Auto-expand next uncompleted step
    if (!completedSteps.includes(stepId)) {
      const nextUncompleted = steps.find(s => s.id > stepId && !newCompleted.includes(s.id));
      if (nextUncompleted) {
        setExpandedStep(nextUncompleted.id);
      }
    }
  };

  const toggleExpand = (stepId) => {
    setExpandedStep(expandedStep === stepId ? -1 : stepId);
  };

  const allCompleted = steps.every(s => completedSteps.includes(s.id));
  const progressPercent = (completedSteps.length / steps.length) * 100;

  // Scroll to verify section when all steps completed
  useEffect(() => {
    if (allCompleted && verifyRef.current) {
      setTimeout(() => {
        verifyRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [allCompleted]);

  const handleVerify = async () => {
    if (!mpstoreId.trim()) {
      toast.error('Masukkan ID MPStore Anda');
      return;
    }

    setVerifying(true);
    setVerificationResult(null);

    try {
      const response = await axios.post(`${API}/seller/verify-mpstore`, {
        mpstore_id: mpstoreId.trim(),
      });

      if (response.data.success) {
        setVerificationResult({
          success: true,
          store_name: response.data.store_name,
          message: response.data.message,
        });
        toast.success('Toko Anda berhasil diverifikasi!');
      } else {
        setVerificationResult({
          success: false,
          message: response.data.message || 'Toko belum memenuhi syarat.',
        });
        toast.error(response.data.message || 'Verifikasi gagal');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || 'Gagal memverifikasi. Coba lagi nanti.';
      setVerificationResult({
        success: false,
        message: msg,
      });
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="seller-onboarding-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div className="flex-1">
            <h1 className="font-heading text-lg md:text-xl font-bold text-gray-900">
              {t('seller_onboarding_title') || 'Cara Berjualan di GIZI.AI'}
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              {completedSteps.length} dari {steps.length} langkah selesai
            </p>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="sticky top-[73px] z-40 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${progressPercent}%`,
                background: allCompleted
                  ? 'linear-gradient(90deg, #10B981, #34D399)'
                  : 'linear-gradient(90deg, #0052CC, #3B82F6)',
              }}
            />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Hero section */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-[#0052CC]/10 text-[#0052CC] px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <Store className="w-4 h-4" />
            Panduan Penjual
          </div>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('seller_onboarding_hero_title') || '5 Langkah Mudah Mulai Berjualan'}
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto">
            {t('seller_onboarding_hero_subtitle') || 'Ikuti panduan berikut untuk mendaftarkan toko Anda di GIZI.AI dan mulai menjangkau ribuan pelanggan.'}
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-4 md:space-y-5 mb-12">
          {steps.map((step, index) => (
            <StepCard
              key={step.id}
              step={step}
              index={index}
              isCompleted={completedSteps.includes(step.id)}
              isExpanded={expandedStep === step.id}
              onToggleComplete={() => toggleStepComplete(step.id)}
              onToggleExpand={() => toggleExpand(step.id)}
            />
          ))}
        </div>

        {/* Verification Section */}
        <div
          ref={verifyRef}
          className={`
            transition-all duration-700 ease-out
            ${allCompleted
              ? 'opacity-100 translate-y-0'
              : 'opacity-40 pointer-events-none translate-y-4'
            }
          `}
        >
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg overflow-hidden">
            {/* Header gradient bar */}
            <div
              className="h-1.5"
              style={{
                background: allCompleted
                  ? 'linear-gradient(90deg, #10B981, #34D399, #6EE7B7)'
                  : 'linear-gradient(90deg, #CBD5E1, #E2E8F0)',
              }}
            />

            <div className="p-6 md:p-8">
              {/* Success state */}
              {verificationResult?.success ? (
                <div className="text-center py-6">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
                    <PartyPopper className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                    Selamat! Toko Anda Terverifikasi
                  </h3>
                  <p className="text-gray-500 mb-2 text-lg">
                    {verificationResult.store_name && (
                      <span className="font-semibold text-gray-700">{verificationResult.store_name}</span>
                    )}
                  </p>
                  <p className="text-gray-500 mb-6">{verificationResult.message}</p>
                  <div className="flex items-center justify-center gap-3">
                    <Link to="/">
                      <Button className="bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-full px-8 py-3">
                        Kembali ke Beranda
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div className={`
                      w-12 h-12 rounded-xl flex items-center justify-center
                      ${allCompleted ? 'bg-emerald-100' : 'bg-gray-100'}
                    `}>
                      <ShieldCheck className={`w-6 h-6 ${allCompleted ? 'text-emerald-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg md:text-xl font-bold text-gray-900">
                        Verifikasi Toko Anda
                      </h3>
                      <p className="text-sm text-gray-500">
                        {allCompleted
                          ? 'Semua langkah selesai! Masukkan ID MPStore untuk verifikasi.'
                          : 'Selesaikan semua langkah di atas terlebih dahulu.'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Error state */}
                  {verificationResult && !verificationResult.success && (
                    <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4 mb-5">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-700">Verifikasi Gagal</p>
                        <p className="text-sm text-red-600 mt-0.5">{verificationResult.message}</p>
                      </div>
                    </div>
                  )}

                  {/* Input form */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <Input
                        value={mpstoreId}
                        onChange={(e) => setMpstoreId(e.target.value)}
                        placeholder="Masukkan ID MPStore Anda (contoh: OK0001)"
                        className="h-12 rounded-xl border-gray-200 focus:border-[#0052CC] focus:ring-2 focus:ring-[#0052CC]/20 text-base"
                        disabled={!allCompleted || verifying}
                        data-testid="mpstore-id-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
                      />
                    </div>
                    <Button
                      onClick={handleVerify}
                      disabled={!allCompleted || verifying || !mpstoreId.trim()}
                      className="h-12 bg-[#0052CC] hover:bg-[#0043A8] text-white rounded-xl px-8 font-semibold text-base"
                      data-testid="verify-mpstore-btn"
                    >
                      {verifying ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Memverifikasi...
                        </>
                      ) : (
                        <>
                          Verifikasi
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    ID MPStore dapat ditemukan di dashboard MPStore Anda.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-16" />
      </main>
    </div>
  );
}
